<template>
	<section class="mini-calendar-container">
		<header class="mini-calendar-header">
			<span class="mini-calendar-month">{{ month }}</span>
			<span class="mini-calendar-nav-arrows">
				<button
					class="mini-calendar-arrow previous"
					@click="
						subtractMonth();
						active = false;
					"
					:disabled="month === initialMonth ? true : false"
				>
					<font-awesome-icon :icon="['fas', 'chevron-left']" />
				</button>
				<button
					class="mini-calendar-arrow next"
					@click="
						addMonth();
						active = false;
					"
					:disabled="month === nextMonth ? true : false"
				>
					<font-awesome-icon :icon="['fas', 'chevron-right']" />
				</button>
				<button
					class="mini-calendar-mobile-toggle"
					:class="{ open: isOpen }"
					@click="toggleCalendar"
				>
					<font-awesome-icon :icon="['far', 'calendar']" />
				</button>
			</span>
		</header>
		<div class="mini-calendar-body" v-show="isOpen">
			<ul class="mini-calendar-days">
				<li v-for="(day, index) in days" :key="index" class="mini-calendar-day">
					{{ day }}
				</li>
			</ul>
			<ul class="mini-calendar-dates">
				<li v-for="(blank, key) in firstDayOfMonth" :key="key"></li>
				<li
					v-for="(date, index) in daysInMonth"
					:key="index + 7"
					class="mini-calendar-date-container"
				>
					<button
						class="mini-calendar-date-selector"
						:class="{
							'current-day': date === initialDate && month === initialMonth,
							active: active === date,
						}"
						@click="
							sendDate(date, month);
							active = date;
						"
					>
						<span class="mini-calendar-date">{{ date }}</span>
						<span
							class="mini-calendar-indicator"
							v-if="
								(month === initialMonth &&
									findDate(postDatesThisMonth, date)) ||
								(month === nextMonth && findDate(postDatesNextMonth, date))
							"
						></span>
					</button>
				</li>
			</ul>
		</div>
	</section>
</template>

<script>
import moment from 'moment';
import { debounce } from 'lodash';

export default {
	name: 'MiniCalendar',
	props: {
		postDatesThisMonth: Array,
		postDatesNextMonth: Array,
	},
	data() {
		return {
			today: moment(),
			dateContext: moment(),
			days: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
			active: false,
			isOpen: false,
			userClickedOpen: false,
		};
	},
	created() {
		window.addEventListener('resize', debounce(this.resizeHandler, 100));
	},
	computed: {
		month() {
			return this.dateContext.format('MMMM');
		},
		daysInMonth() {
			return this.dateContext.daysInMonth();
		},
		currentDate() {
			return this.dateContext.get('date');
		},
		firstDayOfMonth() {
			const firstDay = moment(this.dateContext).subtract(
				this.currentDate - 1,
				'days',
			);
			return firstDay.weekday();
		},
		initialDate() {
			return this.today.get('date');
		},
		initialMonth() {
			return this.today.format('MMMM');
		},
		nextMonth() {
			return moment(this.today).add(1, 'month').format('MMMM');
		},
	},
	destroyed() {
		window.removeEventListener('resize', debounce(this.resizeHandler, 100));
	},
	mounted() {
		this.isOpen = window.innerWidth >= 1024;
		this.$emit('toggleCalendar', this.isOpen);
	},
	methods: {
		addMonth() {
			this.dateContext = moment(this.dateContext).add(1, 'month');
		},
		subtractMonth() {
			this.dateContext = moment(this.dateContext).subtract(1, 'month');
		},
		sendDate(date, month) {
			this.$emit('clicked', date, month);
		},
		toggleCalendar(event) {
			// If an event exists it means it was triggered by a click event.
			//Otherwise it is being handled by the page resize event.
			if (event) {
				if (this.isOpen) {
					this.userClickedOpen = false;
					this.isOpen = false;
				} else {
					this.userClickedOpen = true;
					this.isOpen = true;
				}
			} else {
				this.isOpen = !this.isOpen;
			}
			this.$emit('toggleCalendar', this.isOpen);
		},
		findDate(month, date) {
			return month.includes(date);
		},
		resizeHandler() {
			if (window.innerWidth >= 1024) {
				if (!this.isOpen) {
					this.toggleCalendar();
				}
				this.userClickedOpen = false;
			} else if (
				window.innerWidth < 1024 &&
				this.isOpen &&
				!this.userClickedOpen
			) {
				this.toggleCalendar();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.mini-calendar-container {
	border: $b360-border-width-thin solid $border-color-light;
	border-radius: $b360-border-radius-base;
	background: $white;

	ul {
		list-style-type: none;
		padding-left: 0;
	}
}

.mini-calendar-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: $b360-border-width-thin solid $border-color-light;
	@include rem('padding', 10px 15px);
}

.mini-calendar-nav-arrows {
	display: flex;
	justify-content: space-between;
	@include rem('width', 76px);
}

.mini-calendar-arrow,
.mini-calendar-mobile-toggle {
	border: $b360-border-width-thin solid $border-color-light;
	border-radius: $b360-border-radius-base;
	background-color: $white;
	color: $brand-blue;
	@include rem('width', 34px);
	@include rem('height', 34px);
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0;
	transition: $b360-motion-selection;

	&:hover {
		background-color: $highlight-color;

		&:not(:disabled) {
			cursor: pointer;
		}
	}

	&:disabled {
		background-color: $off-white;
		color: $text-base-color;
	}
}

.mini-calendar-days,
.mini-calendar-dates {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
}

.mini-calendar-days {
	font-size: $b360-font-size--small;
	@include typo--roboto__bold;
}

.mini-calendar-day {
	text-align: center;
	align-items: center;
}

.mini-calendar-date-container {
	@include rem('height', 44px);
	@include rem('padding', 2px 6px);

	&.current-day {
		.mini-calendar-date-selector {
			background-color: $highlight-color;
			color: $brand-blue;
			border: $b360-border-width-thin solid $brand-blue;
			border-radius: $b360-border-radius-base;
		}
	}
}

.mini-calendar-date-selector {
	width: 100%;
	height: 100%;
	background: none;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: center;
	padding: 6px 0 8px;
	line-height: 1;
	font-size: $b360-font-size--small;
	cursor: pointer;
	border: $b360-border-width-thin solid transparent;
	border-radius: $b360-border-radius-base;
	transition: $b360-motion-selection;

	&:not(.current-day):hover,
	&:not(.current-day):focus,
	&:not(.current-day).active {
		border: $b360-border-width-thin solid $border-color-light;
		background: $off-white;

		.mini-calendar-indicator {
			background-color: $subheader-color;
		}
	}

	&.current-day {
		background-color: $highlight-color;
		color: $brand-blue;
		border: $b360-border-width-thin solid $brand-blue;

		.mini-calendar-indicator {
			background-color: $brand-blue;
		}
	}
}

.mini-calendar-indicator {
	display: inline-block;
	@include rem('width', 6px);
	@include rem('height', 6px);
	border-radius: 50%;
	background-color: $b360-color--gray__medium;
	transition: $b360-motion-selection;
}

.suggested-posts-calendar {
	.mini-calendar-body {
		&.is-hidden {
			display: none;
		}
	}

	.mini-calendar-mobile-toggle {
		display: none;
	}

	@include sm {
		.mini-calendar-nav-arrows {
			@include rem(width, 118px);
		}

		.mini-calendar-mobile-toggle {
			display: inline-block;

			&.open {
				background: $brand-blue;
				color: $white;
			}
		}
	}
}
</style>
